import React from 'react';
import { Anchor, Box, Text } from '@mantine/core';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import FilterChip from './components/Filter.Chip';
import FilterPanel from './Filter.Panel';
import { dateSectionTitle, dateWindowLabel } from './filter.date.label';
import { filterOperatorLabel } from './filter.operator.label';
import { filterSummary } from './filter.summary';
import type { TypeDateSection, TypeFilterSection, TypeListSort } from '@/types/list/types.list.filter';
import { formatNumber } from '@/utils/format.number';
import './filter.panel.css';

type Props = {
    sections: TypeFilterSection[];
    active: Record<string, string[]>;
    onToggle: (sectionId: string, value: string) => void;
    excluded?: Record<string, boolean>;
    onExclude?: (sectionId: string, value: boolean) => void;
    onClear: () => void;
    dates?: TypeDateSection[];
    dateValues?: Record<string, string>;
    onDateChange?: (sectionId: string, value: string) => void;
    sort?: TypeListSort;
    shown?: number;
    total?: number;
    searching?: boolean;
};

const NO_EXCLUDED: Record<string, boolean> = {};

const FilterChips: React.FC<Props> = ({
    sections, active, onToggle, excluded = NO_EXCLUDED, onExclude, onClear, dates = [], dateValues = {}, onDateChange, sort, shown, total, searching = false,
}) => {
    const reduced = useReducedMotion();
    const activeCount = Object.values(active).reduce((sum, values) => sum + values.length, 0) + Object.keys(dateValues).length;

    const panel = (initial: string, close: () => void) => (
        <FilterPanel
            sections={sections}
            active={active}
            onToggle={onToggle}
            excluded={excluded}
            onExclude={onExclude}
            onClear={() => { onClear(); close(); }}
            dates={dates}
            dateValues={dateValues}
            onDateChange={onDateChange}
            sort={sort}
            activeCount={activeCount}
            onDone={close}
            initial={initial}
        />
    );

    const chips = [
        ...sections.filter((section) => (active[section.id]?.length ?? 0) > 0).map((section) => {
            const selected = active[section.id];
            const out = Boolean(excluded[section.id]);
            const first = section.options.find((option) => option.value === selected[0]);
            return (
                <FilterChip
                    key={`s:${section.id}`}
                    name={section.label}
                    operator={filterOperatorLabel(selected.length, out)}
                    value={filterSummary(section, selected)}
                    title={selected.map((value) => section.options.find((option) => option.value === value)?.label ?? value).join(', ')}
                    excluded={out}
                    dot={selected.length === 1 ? first?.dot : undefined}
                    onRemove={() => { selected.forEach((value) => onToggle(section.id, value)); onExclude?.(section.id, false); }}
                >
                    {(close) => panel(`s:${section.id}`, close)}
                </FilterChip>
            );
        }),
        ...dates.filter((section) => dateValues[section.id]).map((section) => (
            <FilterChip
                key={`d:${section.id}`}
                name={dateSectionTitle(section.label)}
                operator=""
                value={dateWindowLabel(dateValues[section.id])}
                title={dateWindowLabel(dateValues[section.id])}
                onRemove={() => onDateChange?.(section.id, '')}
            >
                {(close) => panel(`d:${section.id}`, close)}
            </FilterChip>
        )),
    ];

    const counted = shown !== undefined && total !== undefined;
    if (!chips.length && !(searching && counted)) return null;

    return (
        <Box className="bd-fp-chips" aria-label="Filtri attivi">
            <Box className="bd-fp-chips-list" role="list">
                <AnimatePresence initial={false}>
                    {chips.map((chip) => (
                        <motion.div
                            key={chip.key}
                            role="listitem"
                            layout={!reduced}
                            initial={reduced ? false : { opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={reduced ? undefined : { opacity: 0, scale: 0.94 }}
                            transition={{ duration: 0.12, ease: 'easeOut' }}
                        >
                            {chip}
                        </motion.div>
                    ))}
                </AnimatePresence>
            </Box>
            <Box className="bd-fp-chips-tail">
                {counted ? (
                    <Text size="xs" c="dimmed" className="bd-fp-chips-count" aria-live="polite">
                        {formatNumber(shown ?? 0)} di {formatNumber(total ?? 0)}
                    </Text>
                ) : null}
                {chips.length ? <Anchor component="button" type="button" size="xs" c="dimmed" onClick={onClear}>Cancella tutto</Anchor> : null}
            </Box>
        </Box>
    );
};

export default FilterChips;
